/**
 * IELTS Academic Reading band conversion and report data mapping.
 *
 * Used by the IELTS_READING (score report, variant A) and
 * IELTS_READING_DETAIL (detailed feedback report, variant B) result pages,
 * and — through ieltsSkills.mjs — by the listening report, which shares the
 * parts/answer-key model. Kept as a plain module so it can be unit tested
 * with node --test, same as fillTheGapValidation.mjs.
 *
 * Kept in sync with sensay-assessment-admin src/utils/ieltsReading.js so both
 * apps read the /progress-report rows the same way.
 */

import { BLANK_FIELDS, decodeHtmlEntities, joinBlankParts } from "./bracketBlanks.mjs";
import { parseFillTheLetters } from "./fillTheLetters.mjs";
import { parseDragWords } from "./dragWords.mjs";
import { sanitizeAssessmentHtml } from "../utilities/TextSanitizer.js";

/**
 * Official IELTS Academic Reading raw-score boundaries for a 40-question
 * paper: raw correct answers at or above minRaw map to the band.
 */
export const IELTS_ACADEMIC_READING_BAND_TABLE = [
  { minRaw: 39, band: 9 },
  { minRaw: 37, band: 8.5 },
  { minRaw: 35, band: 8 },
  { minRaw: 33, band: 7.5 },
  { minRaw: 30, band: 7 },
  { minRaw: 27, band: 6.5 },
  { minRaw: 23, band: 6 },
  { minRaw: 19, band: 5.5 },
  { minRaw: 15, band: 5 },
  { minRaw: 13, band: 4.5 },
  { minRaw: 10, band: 4 },
  { minRaw: 8, band: 3.5 },
  { minRaw: 6, band: 3 },
  { minRaw: 4, band: 2.5 },
  { minRaw: 2, band: 2 },
  { minRaw: 1, band: 1 },
];

/**
 * Converts a raw correct-answer count into an IELTS reading band.
 *
 * Assessments with a question count other than 40 are scaled onto the
 * 40-question table proportionally, so shorter mock papers still map onto
 * the familiar 0-9 scale. Returns null when the inputs cannot produce a
 * meaningful band (no questions).
 */
export function calculateIeltsReadingBand(correctCount, totalQuestions) {
  if (
    !Number.isFinite(correctCount) ||
    !Number.isFinite(totalQuestions) ||
    totalQuestions <= 0
  ) {
    return null;
  }
  const clamped = Math.min(Math.max(correctCount, 0), totalQuestions);
  const scaled =
    totalQuestions === 40
      ? clamped
      : Math.round((clamped / totalQuestions) * 40);
  const row = IELTS_ACADEMIC_READING_BAND_TABLE.find((r) => scaled >= r.minRaw);
  return row ? row.band : 0;
}

/** "7" -> "7.0", 6.5 -> "6.5"; null -> "-". */
export function formatIeltsBand(band) {
  if (band === null || band === undefined || !Number.isFinite(band)) {
    return "-";
  }
  return band.toFixed(1);
}

/**
 * Maps a band to the descriptor tier used for the i18n keys
 * IELTS_READING_LEVEL1 (lowest) .. IELTS_READING_LEVEL5 (highest).
 */
export function ieltsReadingBandLevel(band) {
  if (band === null || band === undefined || !Number.isFinite(band)) {
    return null;
  }
  if (band >= 8) return 5;
  if (band >= 7) return 4;
  if (band >= 6) return 3;
  if (band >= 5) return 2;
  return 1;
}

/**
 * Whole-band index for the official band names (i18n keys
 * IELTS_BAND_NAME_0 .. IELTS_BAND_NAME_9, e.g. 6 -> "Competent user").
 * Half bands use the name of the band below (6.5 -> 6).
 */
export function ieltsBandNameIndex(band) {
  if (band === null || band === undefined || !Number.isFinite(band)) {
    return null;
  }
  return Math.min(9, Math.max(0, Math.floor(band)));
}

const OPTION_LETTERS = "ABCDEFGHIJKLMNOP".split("");

function parseJsonSafe(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

/** Tags that carry content of their own, with no text between them. */
const EMBED_TAG_PATTERN = /<(img|audio|video|iframe|svg|table)\b/i;

/**
 * True when a passage would render as something a reader can see. Passage text
 * is authored HTML as often as plain text, and an empty authored passage still
 * arrives as a non-empty string — "<p></p>", "<p>&nbsp;</p>", a stray "<br>" —
 * which would otherwise print a "Transcript" heading above an empty box.
 */
export function hasVisiblePassageText(passageText) {
  if (typeof passageText !== "string") return false;
  const raw = passageText;
  if (EMBED_TAG_PATTERN.test(raw)) return true;
  return (
    raw
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;|&#160;|&#xa0;/gi, " ")
      .trim().length > 0
  );
}

/**
 * Extracts the reading passages of a MULTICHOICE_COMBINED definition, in the
 * same priority order the exercise page uses (passages / articles / texts
 * arrays, then the plain text field). Passages with nothing visible in them
 * are dropped, so the transcript and passage sections that render these
 * disappear along with their heading instead of showing an empty box.
 */
export function extractPassages(data) {
  const list = data?.passages || data?.articles || data?.texts;
  if (Array.isArray(list) && list.length) {
    return list
      .map((p, i) => ({
        title: p?.title || null,
        text: p?.text || p?.content || "",
        index: i,
      }))
      .filter((p) => hasVisiblePassageText(p.text));
  }
  if (hasVisiblePassageText(data?.text)) {
    return [{ title: null, text: data.text, index: 0 }];
  }
  return [];
}

/**
 * Best human-readable "correct answer" for a FILL_THE_GAP gap. The stored
 * answer is a regex, so:
 *  - an explicit authoring field (correctAnswer) always wins,
 *  - DROPDOWN gaps show the option that matches the regex,
 *  - text gaps show the pattern only when it is a plain word or a plain
 *    "one|two" alternation (rendered as "one / two"),
 *  - anything else stays hidden (returns null), matching the existing
 *    SCORE_REPORT behavior of never exposing raw regexes.
 */
export function gapCorrectDisplay(gap) {
  if (!gap) return null;
  if (gap.correctAnswer) return String(gap.correctAnswer);

  const pattern = typeof gap.answer === "string" ? gap.answer : null;

  if (gap.fieldType === "DROPDOWN" && Array.isArray(gap.options) && pattern) {
    const match = gap.options.find((option) => {
      try {
        return new RegExp(pattern).test(String(option));
      } catch (error) {
        return false;
      }
    });
    if (match !== undefined) return String(match);
  }

  if (!pattern) return null;
  const stripped = pattern.replace(/^\^/, "").replace(/\$$/, "");
  const alternatives = stripped.split("|").map((a) => a.trim());
  const plain = alternatives.every(
    (a) => a.length > 0 && !/[\\^$.|?*+()[\]{}]/.test(a),
  );
  if (!plain) return null;
  return alternatives.join(" / ");
}

/**
 * Replaces __N__ gap tokens with the report-wide question number of that gap
 * ("[12]"), so the passage references match the answer key numbering.
 */
export function replaceGapTokensForReport(text, gaps, firstQuestionNumber) {
  return String(text || "").replace(/__(\d+)__/g, (token, num) => {
    const index = Number.parseInt(num, 10) - 1;
    if (index < 0 || index >= (gaps?.length || 0)) {
      return token;
    }
    return `<strong class="gap-marker">[${firstQuestionNumber + index}]</strong> ______`;
  });
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Prepares passage text for {@html} rendering. Plain-text passages are
 * HTML-escaped and get <br> line breaks; authored HTML passages are passed
 * through untouched (same trust level as the exercise page, which also
 * renders them via {@html}).
 */
const HTML_TAG_PATTERN =
  /<\/?(p|br|div|span|strong|em|b|i|u|ul|ol|li|h[1-6]|table|thead|tbody|tr|td|th|img|a|mark|sup|sub|blockquote|hr|section|article|font|small|big|center)\b[^>]*>/i;

export function passageToHtml(passageText) {
  const raw = String(passageText || "");
  const isHtml = HTML_TAG_PATTERN.test(raw);
  return {
    html: isHtml ? raw : escapeHtml(raw).replace(/\r?\n/g, "<br>"),
    escaped: !isHtml,
  };
}

/**
 * Wraps the evidence quotes of incorrectly answered questions in numbered
 * <mark> tags. `escaped` says whether the html came from an escaped
 * plain-text passage, so the quote is escaped the same way before matching.
 */
export function markEvidence(html, questions, escaped) {
  let result = String(html || "");
  (questions || [])
    .filter((q) => q && !q.isCorrect && q.evidenceQuote)
    .forEach((q) => {
      const quote = escaped ? escapeHtml(q.evidenceQuote) : q.evidenceQuote;
      const regex = new RegExp(escapeRegExp(quote), "i");
      result = result.replace(
        regex,
        (match) =>
          `<mark class="evidence-mark"><strong class="evidence-number">[${q.number}]</strong> ${match}</mark>`,
      );
    });
  return result;
}

export function buildPassageHtml(passageText, questions) {
  const { html, escaped } = passageToHtml(passageText);
  return markEvidence(html, questions, escaped);
}

function multichoiceQuestion(q, answerEntry, number) {
  const options = OPTION_LETTERS.filter(
    (letter) => q?.["option" + letter],
  ).map((letter) => ({ key: letter, text: String(q["option" + letter]) }));
  const selectedKey = answerEntry?.selectedOption ?? null;
  return {
    number,
    text: q?.question || "",
    options,
    selectedKey,
    selectedDisplay: selectedKey,
    correctKey: q?.correctOption ?? null,
    correctDisplay: q?.correctOption ?? null,
    isCorrect: answerEntry?.correctOption === true,
    answered: selectedKey !== null && selectedKey !== undefined && selectedKey !== "",
    explanation: q?.explanation || null,
    evidenceQuote: q?.evidenceQuote || null,
  };
}

// A plain-text passage is escaped and gets <br> line breaks (passageToHtml),
// applied to the text between the blanks only: done before parsing, it would
// break a marker that spans a line or double-escape its answer. Tag-free text
// from the rich editor is already entity-encoded (`1 &lt; 2`), and the page
// shows it decoded, so it is decoded once before escaping.
function reportParts(passageText, parts) {
  if (!passageToHtml(passageText).escaped) return parts;
  return (parts || []).map((part) =>
    part.blank !== undefined
      ? part
      : { html: escapeHtml(decodeHtmlEntities(part.html)).replace(/\r?\n/g, "<br>") },
  );
}

/**
 * One report part for a FILL_THE_LETTERS or DRAG_WORDS row: a question per
 * word block / slot, numbered from `firstNumber`, and the question and text
 * as passages with each blank swapped for its number.
 */
function textBlankPart(type, data, answerData, firstNumber) {
  const isLetters = type === "FILL_THE_LETTERS";
  // Read the blanks exactly as the exercise page does, so blank i lines up
  // with stored answer i. Report formatting comes after, around the blanks.
  const parsed = isLetters
    ? parseFillTheLetters(data, sanitizeAssessmentHtml)
    : parseDragWords(data, { sanitize: sanitizeAssessmentHtml });
  const blanks = isLetters ? parsed.blocks : parsed.slots;
  const answers =
    (isLetters ? answerData?.letterAnswerList : answerData?.dragAnswerList) || [];

  const questions = blanks.map((blank, i) => {
    const answerText = String(answers[i]?.answer ?? "");
    const correctDisplay = isLetters ? blank.text : blank.answer;
    return {
      number: firstNumber + i,
      text: isLetters ? `Word ${i + 1}` : `Blank ${i + 1}`,
      options: [],
      selectedKey: answerText,
      selectedDisplay: answerText,
      correctKey: correctDisplay,
      correctDisplay,
      isCorrect: answers[i]?.correct === true,
      // A letter answer marks its empty boxes with "_".
      answered: isLetters ? /[^_\s]/.test(answerText) : answerText.trim().length > 0,
      explanation: null,
      evidenceQuote: null,
    };
  });

  const marker = (blank) => {
    const number = `<strong class="gap-marker">[${firstNumber + blank.index}]</strong>`;
    // One underscore per box, with the block's own spaces between them.
    const boxes = isLetters ? blank.parts.map((part) => (part.space ? " " : "_")).join("") : "";
    return isLetters ? `${boxes}${number}` : `${number} ______`;
  };
  const passages = BLANK_FIELDS.filter((name) => hasVisiblePassageText(data?.[name])).map(
    (name, index) => ({
      title: null,
      index,
      text: data[name],
      html: joinBlankParts(reportParts(data[name], parsed.fields[name]), marker),
    }),
  );

  return { questions, passages };
}

/**
 * Builds the render model for the IELTS reading reports from the raw
 * /progress-report rows. Only reading exercise types contribute; intro,
 * verification and speaking/writing items are ignored. Question numbering is
 * continuous across parts, like the 1-40 numbering of a real IELTS paper.
 *
 * Returns { parts, totalQuestions, totalCorrect, percentage, band }.
 */
export function buildIeltsReadingReport(progressList) {
  const parts = [];
  let questionNumber = 0;

  (progressList || []).forEach((item) => {
    if (!item) return;
    const type = item.exerciseType;
    const data = parseJsonSafe(item.data);
    const answerData = parseJsonSafe(item.answerData);

    if (type === "MULTICHOICE_COMBINED") {
      const answers = answerData?.selectedOptionList || [];
      const questions = (data?.questions || []).map((q, i) => {
        questionNumber += 1;
        return multichoiceQuestion(q, answers[i], questionNumber);
      });
      if (!questions.length) return;
      const passages = extractPassages(data).map((p) => ({
        ...p,
        html: buildPassageHtml(p.text, questions),
      }));
      parts.push({
        number: parts.length + 1,
        title: data?.title || item.name || "Part",
        audio: data?.audioFileName || null,
        passages,
        questions,
      });
    } else if (type === "MULTICHOICE_SELECT" || type === "LISTEN_WITH_IMAGE") {
      questionNumber += 1;
      const question = multichoiceQuestion(
        data,
        {
          selectedOption: answerData?.selectedOption,
          correctOption: answerData?.selectedAnswerCorrect === true,
        },
        questionNumber,
      );
      parts.push({
        number: parts.length + 1,
        title: data?.title || item.name || "Part",
        audio: data?.audioFileName || null,
        passages: extractPassages(data).map((p) => ({
          ...p,
          html: buildPassageHtml(p.text, [question]),
        })),
        questions: [question],
      });
    } else if (type === "FILL_THE_GAP") {
      const gaps = data?.gaps || [];
      if (!gaps.length) return;
      const gapAnswers = answerData?.gapAnswerList || [];
      const firstNumber = questionNumber + 1;
      const questions = gaps.map((gap, i) => {
        questionNumber += 1;
        const answerText = gapAnswers[i]?.answer ?? "";
        const options =
          gap?.fieldType === "DROPDOWN" && Array.isArray(gap?.options)
            ? gap.options.map((option) => ({
                key: String(option),
                text: String(option),
              }))
            : [];
        const correctDisplay = gapCorrectDisplay(gap);
        return {
          number: questionNumber,
          text: gap?.label || `Gap ${i + 1}`,
          options,
          selectedKey: answerText,
          selectedDisplay: answerText,
          correctKey: correctDisplay,
          correctDisplay,
          isCorrect: gapAnswers[i]?.correct === true,
          answered: String(answerText).trim().length > 0,
          explanation: gap?.explanation || null,
          evidenceQuote: gap?.evidenceQuote || null,
        };
      });
      const passageSource = hasVisiblePassageText(data?.text)
        ? data.text
        : data?.question;
      const passages = [];
      if (hasVisiblePassageText(passageSource)) {
        const { html, escaped } = passageToHtml(passageSource);
        const withMarkers = replaceGapTokensForReport(html, gaps, firstNumber);
        passages.push({
          title: null,
          index: 0,
          text: passageSource,
          html: markEvidence(withMarkers, questions, escaped),
        });
      }
      parts.push({
        number: parts.length + 1,
        title: data?.title || item.name || "Part",
        audio: data?.audioFileName || null,
        passages,
        questions,
      });
    } else if (type === "FILL_THE_LETTERS" || type === "DRAG_WORDS") {
      const { questions, passages } = textBlankPart(
        type,
        data,
        answerData,
        questionNumber + 1,
      );
      if (!questions.length) return;
      questionNumber += questions.length;
      parts.push({
        number: parts.length + 1,
        title: data?.title || item.name || "Part",
        audio: data?.audioFileName || null,
        passages,
        questions,
      });
    }
  });

  const allQuestions = parts.flatMap((p) => p.questions);
  const totalQuestions = allQuestions.length;
  const totalCorrect = allQuestions.filter((q) => q.isCorrect).length;
  const percentage = totalQuestions
    ? Math.round((totalCorrect / totalQuestions) * 100)
    : null;
  const band = calculateIeltsReadingBand(totalCorrect, totalQuestions);

  return { parts, totalQuestions, totalCorrect, percentage, band };
}
